import { FC, useEffect, useMemo, useState } from 'react';
import { Button, Card, Divider, RadixTable } from '@features/ui';
import {
  periodOptions,
  groupByOptions,
} from '@features/admin/reports/reportData';
import { toast } from 'react-toastify';
import {
  ReportPeriod,
  ReportGroupBy,
  TReportAveragePaymentsByUserGroupData,
} from '@src/types/reports';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { useReportFilters } from '@hooks/useReportFilters';
import { useReportTableColumns } from '@hooks/useReportTableColumns';
import { useReportChartData, getChartColor } from '@hooks/useReportChartData';
import {
  getAveragePaymentsByUserGroup,
  exportAveragePaymentsByUserGroup,
} from '@src/services/reports';
import { getUserGroups } from '@src/services/userGroups';
import { downloadFile } from '@src/lib/utils';
import { canAccess } from '@src/utils/auth';
import { UserRolePermission } from '@src/types/settings';
import { useAuthStore } from '@src/stores/authStore';
import dayjs from 'dayjs';

import { ReportFilteringSettings } from '../ReportFilteringSettings';

interface TooltipProps {
  active?: boolean;
  payload?: Array<{
    value: number;
    dataKey: string;
    color: string;
    payload: any;
  }>;
}

const CustomTooltip: FC<TooltipProps> = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div
        className="bg-card-stat p-2.5 rounded-lg text-xs border border-[#3D3D3D] flex flex-col gap-y-1 min-w-[200px]"
        style={{ boxShadow: '0px 7px 13px 0px #B6D72C14' }}
      >
        <p className="text-[#999999] text-[10px] text-center mb-1">
          {payload[0]?.payload?.date}
        </p>
        {payload.map((entry, index) => (
          <div key={index} className="flex justify-between items-center gap-4">
            <span className="text-gray-300 text-xs">{entry.dataKey}:</span>
            <span
              className="text-white font-semibold"
              style={{ color: entry.color }}
            >
              ${Number(entry.value).toFixed(2)}
            </span>
          </div>
        ))}
      </div>
    );
  }

  return null;
};

type TUserGroupOption = { id: number; name: string };

export const AveragePaymentsByUserGroup: FC = () => {
  const { auth } = useAuthStore();

  // Data state
  const [data, setData] = useState<TReportAveragePaymentsByUserGroupData[]>([]);
  const [selectedGroupNames, setSelectedGroupNames] = useState<string[]>([]);
  const [userGroupsList, setUserGroupsList] = useState<TUserGroupOption[]>([]);

  // Use custom hooks for filter management
  const filters = useReportFilters({
    defaultPeriod: ReportPeriod.LAST_7_DAYS,
    defaultGroupBy: ReportGroupBy.DAY,
    enableComparisons: false,
    enableUserGroups: true,
  });

  // Build user group options for the filter (value is string ID)
  const userGroupOptions = useMemo(
    () => userGroupsList.map((g) => ({ label: g.name, value: String(g.id) })),
    [userGroupsList]
  );

  // Load user groups from API on mount
  useEffect(() => {
    const loadUserGroups = async () => {
      try {
        const response = await getUserGroups();
        setUserGroupsList(response.data || []);
      } catch (error) {
        console.error('Error loading user groups:', error);
      }
    };

    loadUserGroups();
  }, []);

  // Use custom hook for table columns generation
  const { columns, displayItems, tableData } =
    useReportTableColumns<TReportAveragePaymentsByUserGroupData>({
      data,
      selectedItems: selectedGroupNames,
      compareToPreviousPeriod: false,
      compareToSamePeriodPreviousYear: false,
      getAccessField: (item) => item.date,
      getMainDataField: (item, key) => item.user_groups[key] || 0,
      getPreviousPeriodField: () => undefined,
      getPreviousYearField: () => undefined,
      itemsField: 'user_groups',
      columnTitleFormatter: (key) => key,
      valueFormatter: (_, value) => `$${Number(value).toFixed(2)}`,
      includeTotalsRow: true,
    });

  // Use custom hook for chart data transformation
  const chartData = useReportChartData({
    data,
    displayItems,
    compareToPreviousPeriod: false,
    compareToSamePeriodPreviousYear: false,
    getAccessField: (item) => item.date,
    getMainDataField: (item, key) => item.user_groups[key] || 0,
    getPreviousPeriodField: () => undefined,
    getPreviousYearField: () => undefined,
  });

  const handleRunReport = async () => {
    if (filters.selectedUserGroups.length === 0) {
      toast.error('Please select at least one user group');
      return;
    }

    const startDate = filters.startDate
      ? dayjs(filters.startDate).format('YYYY-MM-DD')
      : null;
    const endDate = filters.endDate
      ? dayjs(filters.endDate).format('YYYY-MM-DD')
      : null;

    if (!startDate || !endDate) {
      toast.error('Please select a date range');
      return;
    }

    const userGroupIds = filters.selectedUserGroups.map(Number).filter(Boolean);
    if (userGroupIds.length === 0) {
      toast.error('Please select at least one user group');
      return;
    }

    filters.setIsLoading(true);
    try {
      const response = await getAveragePaymentsByUserGroup({
        start_date: startDate,
        end_date: endDate,
        grouping: (filters.selectedGroupBy?.toLowerCase() ?? 'day') as 'day' | 'week' | 'month' | 'quarter' | 'year',
        user_groups: userGroupIds,
      });

      // Transform periods to the format expected by the hooks (date field)
      const transformedData: TReportAveragePaymentsByUserGroupData[] = response.periods.map((p) => ({
        date: p.period,
        user_groups: p.user_groups as Record<string, number>,
      }));

      // Extract group names from response to use as column keys
      const groupNames = Object.keys(
        transformedData.reduce((acc, row) => ({ ...acc, ...row.user_groups }), {} as Record<string, number>)
      );
      setSelectedGroupNames(groupNames);
      setData(transformedData);
      toast.success('Report generated successfully!');
    } catch (error) {
      toast.error('Error fetching report');
      console.error('Error fetching report:', error);
    } finally {
      filters.setIsLoading(false);
    }
  };

  const handleExportReport = async () => {
    if (filters.selectedUserGroups.length === 0) {
      toast.error('Please select at least one user group to export');
      return;
    }

    const startDate = filters.startDate
      ? dayjs(filters.startDate).format('YYYY-MM-DD')
      : null;
    const endDate = filters.endDate
      ? dayjs(filters.endDate).format('YYYY-MM-DD')
      : null;

    if (!startDate || !endDate) {
      toast.error('Please select a date range');
      return;
    }

    const userGroupIds = filters.selectedUserGroups.map(Number).filter(Boolean);

    try {
      const response = await exportAveragePaymentsByUserGroup({
        start_date: startDate,
        end_date: endDate,
        grouping: (filters.selectedGroupBy?.toLowerCase() ?? 'day') as 'day' | 'week' | 'month' | 'quarter' | 'year',
        user_groups: userGroupIds,
      });

      downloadFile(response.data, 'average-payments-by-user-group');
      toast.success('Report exported successfully!');
    } catch (error) {
      console.error('Error exporting report:', error);
      toast.error('Failed to export report');
    }
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-white">
        Average Payments by User Group
      </h1>

      <Card className="p-6">
        <ReportFilteringSettings
          showUserGroupsFilter
          compareToPreviousPeriod={false}
          compareToSamePeriodPreviousYear={false}
          dateRangeValue={filters.valueStart}
          groupByOptions={groupByOptions}
          isLoading={filters.isLoading}
          periodOptions={periodOptions}
          periodRange={filters.periodRange}
          selectedGroupBy={filters.selectedGroupBy}
          selectedPeriod={filters.selectedPeriod}
          selectedUserGroups={filters.selectedUserGroups}
          showCompareToPreviousPeriod={false}
          showCompareToSamePeriodPreviousYear={false}
          userGroupOptions={userGroupOptions}
          onCompareToPreviousPeriodChange={() => {}}
          onCompareToSamePeriodPreviousYearChange={() => {}}
          onDateRangeChange={filters.setValueStart}
          onGroupByChange={filters.setSelectedGroupBy}
          onPeriodChange={filters.setSelectedPeriod}
          onRunReport={handleRunReport}
          onUserGroupsChange={filters.setSelectedUserGroups}
        />
      </Card>

      <Card className="p-6">
        <div className="space-y-4">
          <ResponsiveContainer height={400} width="100%">
            <LineChart data={chartData}>
              <CartesianGrid stroke="#333" strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                stroke="#999"
                style={{ fontSize: '12px' }}
              />
              <YAxis
                stroke="#999"
                style={{ fontSize: '12px' }}
                tickFormatter={(value) => `$${value.toLocaleString()}`}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend iconType="line" wrapperStyle={{ fontSize: '14px' }} />

              {displayItems.map((group, index) => (
                <Line
                  key={group}
                  activeDot={{ r: 6 }}
                  dataKey={group}
                  dot={{
                    fill: getChartColor(index),
                    r: 4,
                  }}
                  name={group}
                  stroke={getChartColor(index)}
                  strokeWidth={2}
                  type="monotone"
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card className="p-6">
        <div className="space-y-4">
          <RadixTable columns={columns} data={tableData} />
          <Divider className="my-4" />
          {canAccess(UserRolePermission.EXPORT_REPORTS, auth?.user?.permissions) && (
            <div className="flex justify-end">
              <Button
                className="text-[#B6D72C] border-[#B6D72C] hover:bg-[#B6D72C] hover:text-black"
                variant="outline"
                onClick={handleExportReport}
              >
                Export to Excel
              </Button>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};
